/**
 * home-dashboard.tsx — Composition root for the / route.
 *
 * Orchestrates:
 *   1. useDashboardData — wraps useOnboardingPackages (no new API)
 *   2. useAuth — provides role + currentUserId for AttentionRail filtering
 *   3. Derives health counters client-side from enriched list rows
 *   4. Renders HealthStrip → AttentionRail → PipelineStrip
 *   5. Empty-state when 0 packages
 *
 * Role mapping (surfaced in PR body for reviewer):
 *   admin  → blocked + any blocker + assigned to me
 *   editor → owned by me + assigned to me
 *   viewer → assigned to me only
 *   agent  → same as viewer
 *
 * The role from useAuth().role is typed as string (the API returns a raw string).
 * We narrow it to AuthRole with a safe fallback to 'viewer'.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { useAuth } from '../../hooks/use-auth'
import { useDashboardData } from './use-dashboard-data'
import { HealthStrip } from './health-strip'
import { AttentionRail, type AuthRole } from './attention-rail'
import { PipelineStrip } from './pipeline-strip'
import { SamplePackCta } from '../packages/sample-pack-cta'
import { DemoDataBadge } from '../packages/demo-data-badge'
import type { OnboardingPackage } from '@agentwiki/shared'

// GH-268: persist the "Include sample data" toggle across refreshes so users
// who explicitly opted in to seeing demo numbers don't have to re-enable it.
const INCLUDE_SAMPLE_DATA_LS_KEY = 'agentwiki.dashboard.includeSampleData'

function readIncludeSampleData(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(INCLUDE_SAMPLE_DATA_LS_KEY) === 'true'
  } catch {
    return false
  }
}

function writeIncludeSampleData(value: boolean): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(INCLUDE_SAMPLE_DATA_LS_KEY, value ? 'true' : 'false')
  } catch {
    // localStorage may be unavailable (private mode, quota); ignore.
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const VALID_ROLES: AuthRole[] = ['admin', 'editor', 'viewer', 'agent']

function toAuthRole(raw: string | null): AuthRole {
  if (raw && (VALID_ROLES as string[]).includes(raw)) return raw as AuthRole
  // Safest default: fewest permissions — reviewer attention only
  return 'viewer'
}

function deriveHealthCounters(
  packages: OnboardingPackage[],
  currentUserId: string | null,
): { blockedCount: number; pendingApprovals: number; activeReviews: number } {
  let blockedCount = 0
  let pendingApprovals = 0
  let activeReviews = 0

  for (const pkg of packages) {
    // Blocked: lifecycle flag OR trust.state blocked OR completeness has-failed
    if (
      pkg.lifecycle === 'blocked' ||
      pkg.trust?.state === 'blocked' ||
      pkg.completeness?.state === 'has-failed'
    ) {
      blockedCount++
    }

    // Pending approvals: reviewer assigned but package not yet approved/released
    if (
      pkg.assignedReviewerUserId &&
      pkg.lifecycle !== 'approved' &&
      pkg.lifecycle !== 'released'
    ) {
      pendingApprovals++
    }

    // Active reviews: current user is the assigned reviewer
    if (currentUserId && pkg.assignedReviewerUserId === currentUserId) {
      activeReviews++
    }
  }

  return { blockedCount, pendingApprovals, activeReviews }
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function DashboardSkeleton({ isDark }: { isDark: boolean }) {
  return (
    <div role="status" aria-label="Loading dashboard" className="flex flex-col gap-6">
      {/* HealthStrip skeleton */}
      <div className="flex gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={cn(
              'h-20 flex-1 animate-pulse rounded-xl border',
              isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-neutral-100',
            )}
          />
        ))}
      </div>
      {/* AttentionRail skeleton */}
      <div
        className={cn(
          'h-32 animate-pulse rounded-xl border',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-neutral-100',
        )}
      />
      {/* PipelineStrip skeleton */}
      <div
        className={cn(
          'h-20 animate-pulse rounded-xl border',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-neutral-100',
        )}
      />
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────

function DashboardEmptyState({ isDark }: { isDark: boolean }) {
  const { t } = useTranslation('dashboard')

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
      <div
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-2xl',
          isDark ? 'bg-surface-2' : 'bg-neutral-100',
        )}
        aria-hidden="true"
      >
        <span className="text-2xl">📦</span>
      </div>
      <div className="space-y-1">
        <h2
          className={cn(
            'text-base font-semibold',
            isDark ? 'text-neutral-100' : 'text-neutral-900',
          )}
        >
          {t('empty.title')}
        </h2>
        <p className={cn('max-w-xs text-sm', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
          {t('empty.body')}
        </p>
      </div>
      {/* GH-268: 2-button CTA — primary "Create your first package" + secondary
          "Try it with sample data" (seeds 3 demo packages and starts the tour). */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/packages"
          className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500"
        >
          {t('empty.cta')}
        </Link>
        <SamplePackCta variant="secondary" />
      </div>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HomeDashboard() {
  const { t } = useTranslation('dashboard')
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  // GH-268: localStorage-backed toggle. Default OFF so counters reflect real
  // onboarding work; users can opt in to inflated numbers when exploring.
  const [includeSampleData, setIncludeSampleData] = useState<boolean>(() =>
    readIncludeSampleData(),
  )
  useEffect(() => {
    writeIncludeSampleData(includeSampleData)
  }, [includeSampleData])

  const { packages, allPackages, templateCount, isLoading, isError } = useDashboardData({
    includeSampleData,
  })
  const { user, role } = useAuth()

  const authRole = toAuthRole(role)
  const currentUserId = user?.id ?? null

  if (isLoading) {
    return (
      <div className={cn('flex h-full flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
        <div className="flex-1 p-6">
          <DashboardSkeleton isDark={isDark} />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className={cn('text-sm', isDark ? 'text-red-400' : 'text-red-600')}>
          Failed to load dashboard. Please refresh.
        </p>
      </div>
    )
  }

  // Empty-state when no real packages AND no demo templates exist. We deliberately
  // gate on `allPackages` (not `packages`) so seeding sample data immediately
  // exits the empty-state once the list query refreshes.
  if (allPackages.length === 0) {
    return (
      <div
        className={cn('flex h-full flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}
        data-tour="home-dashboard"
      >
        <div className="flex-1">
          <DashboardEmptyState isDark={isDark} />
        </div>
      </div>
    )
  }

  const { blockedCount, pendingApprovals, activeReviews } = deriveHealthCounters(
    packages,
    currentUserId,
  )

  return (
    <div
      className={cn('flex h-full flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}
      data-tour="home-dashboard"
    >
      {/* Page header */}
      <div
        className={cn(
          'flex items-center gap-3 border-b px-6 py-4',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
        )}
      >
        <div className="flex-1">
          <h1
            className={cn(
              'text-base font-semibold',
              isDark ? 'text-neutral-100' : 'text-neutral-900',
            )}
          >
            {/* Use workspace label — i18n health.title describes the strip, not the page */}
            AgentWiki
          </h1>
          <p className={cn('text-xs', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
            {t('health.title')}
          </p>
        </div>

        {/* GH-268: Include-sample-data toggle. Only render when at least one
            template exists; hidden in pure-real workspaces to reduce noise. */}
        {templateCount > 0 && (
          <label
            className={cn(
              'flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1 text-xs',
              isDark ? 'text-neutral-300 hover:bg-surface-2' : 'text-neutral-700 hover:bg-neutral-100',
            )}
            title={t('sampleData.includeTooltip')}
          >
            <input
              type="checkbox"
              checked={includeSampleData}
              onChange={(e) => setIncludeSampleData(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
              data-testid="include-sample-data-toggle"
            />
            <span>{t('sampleData.includeLabel')}</span>
            {includeSampleData && <DemoDataBadge size="sm" />}
          </label>
        )}
      </div>

      {/* Dashboard body */}
      <div className="flex flex-col gap-6 overflow-y-auto p-6">
        <HealthStrip
          totalPackages={packages.length}
          blockedCount={blockedCount}
          pendingApprovals={pendingApprovals}
          activeReviews={activeReviews}
        />

        <AttentionRail
          packages={packages}
          role={authRole}
          currentUserId={currentUserId ?? ''}
        />

        <PipelineStrip packages={packages} />
      </div>
    </div>
  )
}
